import { Avatar, Box, ButtonBase, Menu, MenuItem, styled } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import { useRef, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useTranslation } from "src/design-system/core/TranslationContext";
import { useStorageQueryString } from "src/hooks";
import useAuth from "src/hooks/useAuth";
import { ACCOUNT_MENU_TK, SHARED_LAYOUT_NAMESPACE } from "src/layouts/SharedLayout/config";
import { PATH_SUPERADMIN } from "src/routesConstants";
import { getInitialUser } from "src/utils/getInitials";
import { isAdminUser } from "src/utils/permission";
import { accountRoutes } from "src/views/account/routes";
import { VIEW_AS_ADMIN_LOCAL_PARAM } from "src/views/admin/AdminView/user/UserFormView/UserFormView.hook";

const useStyles = makeStyles(() => ({
    popover: {
        width: 250,
    },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    height: 32,
    width: 32,
    backgroundColor: theme.palette.primary.main,
    fontSize: 15,
}));

const Account: FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const { t } = useTranslation(`${SHARED_LAYOUT_NAMESPACE}:${ACCOUNT_MENU_TK}`);
    const ref = useRef<any>(null);
    const { user, logout, userAdminId, viewAsAdmin } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [isOpen, setOpen] = useState<boolean>(false);

    const handleOpen = (): void => {
        setOpen(true);
    };

    const handleClose = (): void => {
        setOpen(false);
    };

    const handleLogout = async (): Promise<void> => {
        try {
            handleClose();
            await logout();
            history.push("/");
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Unable to logout", {
                variant: "error",
            });
        }
    };

    const handleBackToAdmin = async () => {
        await viewAsAdmin(userAdminId);
        history.push(PATH_SUPERADMIN);
    };

    const isDisplaySuperAdminMode = useStorageQueryString(VIEW_AS_ADMIN_LOCAL_PARAM);

    // TODO : this should be moved to the user context
    const handleDisplaySuperAdminMode = async () => {
        localStorage.setItem(
            `flynt:${VIEW_AS_ADMIN_LOCAL_PARAM}`,
            (!isDisplaySuperAdminMode).toString()
        );
        //refresh page
        window.location.reload();
    };

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                component={ButtonBase}
                onClick={handleOpen}
                {...{ ref }}
            >
                <StyledAvatar variant="circular">{user && getInitialUser(user)}</StyledAvatar>
            </Box>
            <Menu
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                keepMounted
                PaperProps={{ className: classes.popover }}
                anchorEl={ref.current}
                open={isOpen}
            >
                <MenuItem component={RouterLink} to={accountRoutes.profileView()}>
                    {t("my-account")}
                </MenuItem>

                {!isAdminUser(user) && (
                    <MenuItem component={RouterLink} to="/app/organization/details">
                        {t("my-organization")}
                    </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>{t("disconnect")}</MenuItem>
                {!isAdminUser(user) &&
                    userAdminId && [
                        <MenuItem key="monitoring" onClick={handleDisplaySuperAdminMode}>
                            {isDisplaySuperAdminMode
                                ? t("not-display-superadmin-mode")
                                : t("display-superadmin-mode")}
                        </MenuItem>,
                        <MenuItem key="sa" onClick={handleBackToAdmin}>
                            {t("back-to-superadmin")}
                        </MenuItem>,
                    ]}
            </Menu>
        </>
    );
};

export default Account;
